import Vendor from 'data/Vendor';
import { BusinessUnit } from 'data/BusinessUnit';
import * as User from 'data/User';

interface SelectionResult {
  vendor: Vendor | null;
  businessUnit: BusinessUnit | null;
}

interface SelectionHandlerParams {
  vendors: Vendor[];
  businessUnits: BusinessUnit[];
  user: User.default;
  isAdminView: boolean;
  restoredSelections?: { vendor: Vendor; businessUnit: BusinessUnit } | null;
}

/**
 * List of page paths where the business unit selector should be disabled
 * These pages typically show data that is not business unit specific
 */
const BUSINESS_UNIT_DISABLED_PAGES = [
  '/warehouses',
  '/categories',
  '/vendors',
  '/users'
] as const;

/**
 * Checks if the current path corresponds to a page where the business unit selector should be disabled
 * 
 * @param currentPath - The current route path (e.g., from usePath() hook)
 * @returns boolean - true if business unit selector should be disabled, false otherwise
 * 
 * @example
 * ```typescript
 * const path = usePath();
 * const shouldDisable = shouldDisableBusinessUnitSelector(path);
 * ```
 */
export function shouldDisableBusinessUnitSelector(currentPath: string | null): boolean {
  if (!currentPath) {
    return false;
  }

  return BUSINESS_UNIT_DISABLED_PAGES.some(disabledPath => 
    currentPath.startsWith(disabledPath)
  );
}

/**
 * Utility functions for handling vendor and business unit selection logic
 * based on user role and admin view state
 */
export class VendorBusinessUnitSelectionHandler {
  
  /**
   * Handles selection logic for vendor users
   * Vendor users should always use their assigned business units, ignore restored selections
   */
  static handleVendorUserSelection({
    vendors,
    businessUnits
  }: Omit<SelectionHandlerParams, 'user' | 'restoredSelections' | 'isAdminView'>): SelectionResult {
    if (businessUnits.length > 0) {
      // Use first business unit's vendor but select "All" business units by default
      const firstBusinessUnit = businessUnits[0];
      const associatedVendor = vendors.find(vendor => vendor.id === firstBusinessUnit.vendorId);
      
      return {
        vendor: associatedVendor || null,
        businessUnit: null // Select "All" business units instead of specific one
      };
    } else {
      // No business units, use first vendor
      return {
        vendor: vendors[0] || null,
        businessUnit: null
      };
    }
  }

  /**
   * Handles selection logic for brand level role users
   * Brand level users should always use their assigned business units, ignore restored selections
   */
  static handleBrandLevelUserSelection({
    vendors,
    businessUnits
  }: Omit<SelectionHandlerParams, 'user' | 'restoredSelections' | 'isAdminView'>): SelectionResult {
    if (businessUnits.length === 0) {
      return { vendor: null, businessUnit: null };
    }

    // Use first business unit's vendor but select "All" business units by default
    const firstBusinessUnit = businessUnits[0];
    const associatedVendor = vendors.find(vendor => vendor.id === firstBusinessUnit.vendorId);
    
    return {
      vendor: associatedVendor || null,
      businessUnit: null // Select "All" business units instead of specific one
    };
  }

  /**
   * Handles selection logic for admin users
   */
  static handleAdminUserSelection({
    user,
    isAdminView,
    restoredSelections,
    vendors
  }: Pick<SelectionHandlerParams, 'user' | 'isAdminView' | 'restoredSelections'> & { vendors: Vendor[] }): SelectionResult {
    // For admin users, only restore selections if not in admin view and they're OFMP admin
    if (!isAdminView && (User.isOfmpAdmin(user) || User.hasOfmpAdminProfile(user))) {
      if (restoredSelections) {
        // Check if the restored vendor still exists in the current vendor list
        const vendorStillExists = vendors.some(vendor => vendor.id === restoredSelections.vendor.id);
        if (vendorStillExists) {
          return restoredSelections;
        }
        // If restored vendor no longer exists, fall through to select first vendor
      }

      // Select first vendor from the list when no valid restored selection
      return {
        vendor: vendors.length > 0 ? vendors[0] : null,
        businessUnit: null // Select "All" business units
      };
    }

    return { vendor: null, businessUnit: null };
  }

  /**
   * Main selection handler that routes to appropriate handler based on user role
   * Only OFMP admin users can have restored selections applied
   */
  static handleSelection(params: SelectionHandlerParams): SelectionResult {
    const { user } = params;
    
    if (User.isVendor(user)) {
      // Vendor users: ignore restored selections, always use their assigned business units
      return this.handleVendorUserSelection(params);
    }
    
    if (User.isBrandLevelRoles(user)) {
      // Brand level users: ignore restored selections, always use their assigned business units
      return this.handleBrandLevelUserSelection(params);
    }
    
    // OFMP admin users: can have restored selections
    return this.handleAdminUserSelection({
      ...params,
      vendors: params.vendors
    });
  }
}